import { router, usePage } from '@inertiajs/vue3';
import type { ComputedRef, Ref } from 'vue';
import { computed, onMounted, ref } from 'vue';
import type { Appearance, ResolvedAppearance } from '@/types';

export type { Appearance, ResolvedAppearance };

export type UseAppearanceReturn = {
    appearance: Ref<Appearance>;
    resolvedAppearance: ComputedRef<ResolvedAppearance>;
    updateAppearance: (value: Appearance) => void;
};

export function updateTheme(value: Appearance): void {
    if (typeof window === 'undefined') {
        return;
    }

    if (value === 'system') {
        const mediaQueryList = window.matchMedia(
            '(prefers-color-scheme: dark)',
        );
        const systemTheme = mediaQueryList.matches ? 'dark' : 'light';

        document.documentElement.classList.toggle(
            'dark',
            systemTheme === 'dark',
        );
    } else {
        document.documentElement.classList.toggle('dark', value === 'dark');
    }
}

const setCookie = (name: string, value: string, days = 365) => {
    if (typeof document === 'undefined') {
        return;
    }

    const maxAge = days * 24 * 60 * 60;

    document.cookie = `${name}=${value};path=/;max-age=${maxAge};SameSite=Lax`;
};

const mediaQuery = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    return window.matchMedia('(prefers-color-scheme: dark)');
};

const getStoredAppearance = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    return localStorage.getItem('appearance') as Appearance | null;
};

const prefersDark = (): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const handleSystemThemeChange = () => {
    const currentAppearance = getStoredAppearance();

    updateTheme(currentAppearance || 'system');
};

export function initializeTheme(): void {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        const savedAppearance = getStoredAppearance();
        updateTheme(savedAppearance || 'light');

        const mq = mediaQuery();
        if (mq && typeof mq.addEventListener === 'function') {
            mq.addEventListener('change', handleSystemThemeChange);
        }
    } catch {
        // Safari e outros browsers podem falhar em contextos restritos (iframe, privado)
    }
}

export function useAppearance(): UseAppearanceReturn {
    const page = usePage();
    const initialFromServer = (page.props.appearance as Appearance) || 'light';
    const appearance = ref<Appearance>(initialFromServer);

    onMounted(() => {
        const savedAppearance = (localStorage.getItem('appearance') || page.props.appearance) as Appearance | null;

        if (savedAppearance && ['light', 'dark', 'system'].includes(savedAppearance)) {
            appearance.value = savedAppearance;
        }
    });

    const resolvedAppearance = computed<ResolvedAppearance>(() => {
        if (appearance.value === 'system') {
            return prefersDark() ? 'dark' : 'light';
        }

        return appearance.value;
    });

    const isLoggedIn = computed(() => !!(page.props.auth as { user?: unknown })?.user);

    function updateAppearance(value: Appearance) {
        appearance.value = value;
        localStorage.setItem('appearance', value);
        setCookie('appearance', value);
        updateTheme(value);

        if (isLoggedIn.value) {
            router.patch('/settings/appearance', { appearance: value }, { preserveScroll: true });
        }
    }

    return {
        appearance,
        resolvedAppearance,
        updateAppearance,
    };
}
